import React, { useEffect, useMemo, useState } from 'react';
import { UserLiteData } from '../../../api/ApiTypes';
import { uuid } from '../../../CodesThatsGottaGoSomewhereElse';
import { CommandDispatcher } from '../../../concepts/commands/CommandDispatcher';
import { usePrompt } from '../../../ui';
import { PickerItem, PickerMenu } from '../../../ui/PickerMenu';
import { Camera } from '../../canvas/Camera';
import { beginMutating } from '../../canvas/commands/ManipulationCommands';
import { Physical } from '../../canvas/entities/Physical';
import { InlineWidget } from '../../canvas/workspace/widgets/WidgetInterface';
import { WidgetManager } from '../../canvas/workspace/widgets/WidgetManager';
import { SelectUserView } from '../../common/SelectUserView';
import { useResolve } from '../../Container';
import { Flexer } from '../../dashboard/elements/LayoutElements';
import { ProfileModel } from '../../ProfileModel';
import { Assignable } from '../assignment/Assignable';
import { AssignmentCreateView } from '../assignment/TaskCreateWidget';
import { AtsertWidget, AtsertWidgetProps } from '../mentions/ext/MentionModule';
import { Commentable } from './Commentable';
import { commentsOpen } from './CommentCommands';
import { InlineTagView } from './SelectTagView';
import { TagSupport } from './TagSupport';

export const AtsertAnnotationWidget = ({ widget }: { widget: InlineWidget }) => {
    const subject = widget.subject as Physical;
    const { denotation, query, onAction } = widget as unknown as AtsertWidgetProps;

    const me = useResolve(ProfileModel);
    const dispatch = useResolve(CommandDispatcher);
    const [mode, setMode] = useState<string>(null);

    useEffect(() => {
        if (subject.is(TagSupport)) {
            if (denotation === '#') {
                setMode('tag');
            }
            else if (denotation === '@' && !!query) {
                setMode('mention');
            }
            else {
                setMode('menu');
            }
        }
        else {
            setMode('mention');
        }
    }, [query]);

    const onSelect = (tm: PickerItem) => {
        if (tm.id === 'comment') {
            onAction('reject');
            dispatch(commentsOpen, [subject.id, true]);
        }
        if (tm.id === 'tag') {
            onAction('swap', { to: '#' });
        }
        else {
            setMode(tm.id);
        }
    };

    const onSelectForMention = (user: UserLiteData) => {
        onAction('insert', {
            fromId: me.id,
            toId: user.id,
            value: user.name,
            tag: uuid('nodash').slice(0, 8),
        });
        // If using mouse, focus will be lost so refire mutate on the subject
        dispatch(beginMutating, [subject.id]);
    };

    const onDone = () => {
        onAction('reject');
    };

    const promptRef = usePrompt({
        ignoreEnter: true,
        onAction: onDone,
    });

    const availableActions = useMemo(() => (
        [
            ...(subject.is(Assignable) ? [{ id: 'assign', icon: 'deqo://icons/dash/actions@[1.2,1.2,0]', text: 'Assign Action' }] : []),
            { id: 'mention', icon: 'deqo://icons/hero/at-symbol', text: 'Mention' },
            ...(subject.is(TagSupport) ? [{ id: 'tag', icon: 'deqo://icons/hero/tag', text: 'Add Tag' }] : []),
            ...(subject.is(Commentable) ? [{ id: 'comment', icon: 'deqo://icons/d2/comment-small', text: 'Add Comment' }] : []),
        ]
    ), [subject]);

    return <Flexer ref={promptRef} className="ignore-adapter">
        {mode === 'menu' && <PickerMenu allowKeyboard items={availableActions} onAction={onSelect} />}
        {mode === 'assign' && <AssignmentCreateView targetIds={[subject.id]} onClose={onDone} />}
        {mode === 'mention' && <SelectUserView title="Mention:" allowKeyboard search={query} onSelect={onSelectForMention} />}
        {mode === 'tag' && <InlineTagView filter={query} targetIds={[subject.id]} onClose={onDone} />}
    </Flexer>;
};

export interface BridgingWidgetInfo {
    name: string;
    ui: React.ComponentType<{ widget: InlineWidget }>;
}

export const createQuillAtsertWidgetBridge = (camera: Camera, widgets: WidgetManager, { name, ui }: BridgingWidgetInfo, subject: Physical): AtsertWidget => {
    return {
        show({ absolutePosition: at, ...props }) {
            at = camera.toModel(camera.toViewFromScreen(at));
            widgets.delete(name);
            const onClose = () => widgets.delete(name);
            const cont = widgets.createContainer({
                name,
                position: ['absolute', { at, relativeTo: 'model' }]
            });
            cont.add(widgets.createInline({ ui, subject, ...props, onClose }));
            widgets.add(cont);
        },
        hide() {
            widgets.delete(name);
        },
    };
};

export const useAtsertWidgetBridge = (info: BridgingWidgetInfo, subject: Physical) => {
    const camera = useResolve(Camera);
    const widgets = useResolve(WidgetManager);
    return useMemo(() => createQuillAtsertWidgetBridge(camera, widgets, info, subject), [info, subject]);
};
